import {
  assert,
  makeLookup,
  schConvertTrialRequest,
  schCreateStripeSubscriptionRequest,
  schCreateStripeSubscriptionResponse,
} from '@aloft/toolkit-shared';
import { Button } from '@aloft/toolkit-sui';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { useToolkitControllerGlobal } from '../../../../controller/hooks/useToolkitControllerGlobal.ts';
import {
  useConvertTrial,
  useCreateStripeSubscription,
} from '../../../../hooks/api/toolkitComponents.ts';
import { useToaster } from '../../../../hooks/useToaster/useToaster.tsx';
import { SubscriptionModuleMode } from '../../types.ts';
import { CheckoutProps } from './types.ts';
import { useConfirmIntent } from './useConfirmIntent.ts';

const getSubmitText = makeLookup<SubscriptionModuleMode, string>({
  cases: [
    ['legacy-new-user-trial', 'Start free trial'],
    ['subscription-expired', 'Subscribe'],
    ['add-payment-method', 'Subscribe'],
  ],
});

type Props = {
  priceID: CheckoutProps['priceID'];
  redirectURL: CheckoutProps['redirectURL'];
  promoCode: string | null;
  mode: SubscriptionModuleMode;
  onCheckoutComplete: CheckoutProps['onCheckoutComplete'];
};

export function CheckoutForm(props: Props) {
  const { priceID, redirectURL, promoCode, mode, onCheckoutComplete } = props;

  const toast = useToaster();
  const stripe = useStripe();
  const elements = useElements();
  const createStripeSubscription = useCreateStripeSubscription({
    onError: (error) => {
      console.error('Could not create Stripe subscription.', JSON.stringify(error, null, 2));
      toast({
        id: 'create-subscription-error',
        type: 'error',
        title: 'Could not complete payment',
        body: 'There was an error processing your payment. Please try again.',
      });
    },
  });
  const confirmIntent = useConfirmIntent({
    onError: (error) => {
      console.error('Could not confirm Stripe intent.', JSON.stringify(error, null, 2));
      toast({
        id: 'confirm-intent-error',
        type: 'error',
        title: 'Could not complete payment',
        body: 'There was an error processing your payment. Please try again.',
      });
    },
  });
  const convertTrial = useConvertTrial({
    onError: (error) => {
      console.error('Could not convert trial.', JSON.stringify(error, null, 2));
      toast({
        id: 'convert-trial-error',
        type: 'error',
        title: 'Could not complete payment',
        body: 'There was an error processing your payment. Please try again.',
      });
    },
  });
  const { subscription } = useToolkitControllerGlobal();

  if (!stripe || !elements) return null;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    // validate the payment element
    const { error: submitError } = await elements.submit();
    if (submitError) return;

    if (mode === 'add-payment-method') {
      assert(
        subscription.hasActiveSubscription && subscription.type === 'trialing',
        "Cannot use subscription module in 'add-payment-method' mode with non-trial subscription.",
      );

      await confirmIntent.mutateAsync({
        stripe,
        elements,
        paymentIntentType: 'setup',
        clientSecret: subscription.pendingSetupIntent,
        redirectURL,
      });

      await convertTrial.mutateAsync({
        body: schConvertTrialRequest.parse({
          priceID,
          promotionCode: promoCode ?? undefined,
        }),
      });
    } else {
      // create the subscription
      const subscriptionRes = await createStripeSubscription.mutateAsync({
        body: schCreateStripeSubscriptionRequest.parse({
          priceID,
          promotionCode: promoCode ?? undefined,
          addTrial: mode === 'legacy-new-user-trial',
        }),
      });

      const { clientSecret, paymentIntentType } =
        schCreateStripeSubscriptionResponse.parse(subscriptionRes);

      // confirm the setup intent for trials and the payment intent for regular subscriptions
      await confirmIntent.mutateAsync({
        stripe,
        elements,
        paymentIntentType,
        clientSecret,
        redirectURL,
      });
    }

    onCheckoutComplete();
  };

  const submitText = getSubmitText(mode);
  const isSubmitDisabled =
    !stripe ||
    !elements ||
    createStripeSubscription.isLoading ||
    confirmIntent.isLoading ||
    convertTrial.isLoading;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <PaymentElement
        options={{
          wallets: {
            // NOTE: These will not be available locally over HTTP
            applePay: 'auto',
            googlePay: 'auto',
          },
          paymentMethodOrder: ['card', 'apple_pay', 'google_pay'],
        }}
      />
      <Button
        type="submit"
        text={submitText}
        disabled={isSubmitDisabled}
        isLoading={isSubmitDisabled}
        onClick={() => ({})}
      />
    </form>
  );
}
